import { useState } from "react";
import { Send } from "lucide-react";
import { Button } from "@/ui/button";
import { Textarea } from "@/ui/textarea";
import {
  usePublicGalleryComments,
  usePublicGalleryMutations,
  type PublicGalleryScope,
} from "@/features/gallery/api";
import type { PublicGalleryAsset } from "@/features/gallery/types";

export function CustomerComments({
  scope,
  asset,
  canComment,
}: {
  scope: PublicGalleryScope;
  asset: PublicGalleryAsset;
  canComment: boolean;
}) {
  const comments = usePublicGalleryComments(scope, asset.assetId);
  const mutations = usePublicGalleryMutations(scope);
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  return (
    <section className="space-y-3 border-t pt-4">
      <h3 className="text-sm font-semibold">Trao đổi về ảnh</h3>
      <div className="max-h-52 space-y-2 overflow-y-auto">
        {comments.isLoading ? (
          <p className="text-sm text-muted-foreground">Đang tải trao đổi</p>
        ) : null}
        {(comments.data ?? []).map((comment) => (
          <div key={comment.id} className="rounded-md border bg-surface p-3 text-sm">
            <div className="flex justify-between gap-3 text-xs">
              <strong>{comment.actorName}</strong>
              <span className="text-muted-foreground">
                {new Date(comment.createdAt).toLocaleString("vi-VN")}
              </span>
            </div>
            <p className="mt-1 whitespace-pre-wrap break-words">{comment.body}</p>
            {comment.versionId ? (
              <span className="mt-1 block text-xs text-muted-foreground">
                {comment.versionId === asset.version.id
                  ? `Phiên bản ${asset.version.versionNumber}`
                  : "Phiên bản trước"}
              </span>
            ) : null}
          </div>
        ))}
      </div>
      {canComment ? (
        <div className="space-y-2">
          <Textarea
            aria-label="Nội dung bình luận"
            value={body}
            maxLength={2000}
            onChange={(event) => setBody(event.target.value)}
          />
          <Button
            type="button"
            size="sm"
            disabled={!body.trim() || mutations.comment.isPending}
            onClick={async () => {
              setError("");
              try {
                await mutations.comment.mutateAsync({
                  assetId: asset.assetId,
                  body: { versionId: asset.version.id, body: body.trim() },
                });
                setBody("");
              } catch (cause) {
                setError((cause as Error).message);
              }
            }}
          >
            <Send /> Gửi bình luận
          </Button>
        </div>
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </section>
  );
}
